'use client';

import { type CustomFormApi, Form } from 'informed';
import { usePathname } from 'next/navigation';
import type React from 'react';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AnimatedItem,
  AnimatedWrapper,
} from '@/components/animation/AnimatedItem';
import { ButtonClose } from '@/components/buttons';
import { Input } from '@/components/form/Input';
import { BOTTOM_GAP } from '@/components/general/Header/HeaderMobile/ModalNav';
import { IconComponent } from '@/components/IconComponent';
import { VerticalAlign } from '@/components/Modal/styled';
import { ModalSelectContainer } from '@/components/ModalSelect';
import { StaticModal } from '@/components/StaticModal';
import { useDebounce } from '@/hooks/debounce';
import { sendSearchOverallAppearanceEvent } from '@/modules/googleAnalytics/events/searchGamesEvents';
import { useGetCommonStore } from '@/store';
import { ResultWithSimilar } from './components/ResultWithSimilar';
import { ScSearch, ScSearchForm, ScSearchModal } from './styled';

interface ISearchForm {
  search: string;
}

export const Search = () => {
  const { t } = useTranslation('common');

  const pathname = usePathname();

  const [isSearchOpened, setSearchOpened] = useState(false);

  const handleOpenSearch = useCallback(() => {
    setSearchOpened(true);
  }, []);

  useEffect(() => {
    setSearchOpened(false);
  }, [pathname]);

  return (
    <ScSearch>
      <button className="button-search" onClick={handleOpenSearch}>
        <div className="value">{t('game.searchGame')}</div>
        <div className="icon">
          <IconComponent className="magnifier-icon" name="magnifier" />
        </div>
      </button>
      {isSearchOpened && (
        <SearchModalStatic
          onClose={() => {
            setSearchOpened(false);
          }}
        />
      )}
    </ScSearch>
  );
};

interface ISearchModalProps {
  padding?: string;
  onClose?: () => void;
  header?: ReactNode;
  isNoPaddingMob?: boolean;
}

const SearchModal: FC<ISearchModalProps> = ({
  onClose,
  header,
  isNoPaddingMob,
}) => {
  const { t } = useTranslation('common');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const formApiRef = useRef<CustomFormApi<ISearchForm>>();

  const [searchPhrase, setSearchPhrase] = useState('');
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const handleChangeResult = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(e.target.value);
  };

  const handleResetSearch = () => {
    if (formApiRef.current == null) return;

    setIsInputFocused(false);
    formApiRef.current.reset();
    setSearchPhrase('');
  };
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    }, 200);

    return () => clearTimeout(timeoutId);
  }, []);

  const searchTextDebounce = useDebounce(searchPhrase, 1000);

  return (
    <ScSearchModal isNoPaddingMob={isNoPaddingMob}>
      <ScSearchForm isFormFocused={isInputFocused}>
        {header && (
          <div className="modal-title">
            {header}
            {onClose && <ButtonClose isStatic onClick={onClose} />}
          </div>
        )}
        <Form<ISearchForm> action="#" formApiRef={formApiRef}>
          <Input
            autoComplete="off"
            autoFocus
            name="search"
            onBlur={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              timeoutRef.current = setTimeout(() => {
                setIsInputFocused(false);
              }, 100);
            }}
            onChange={handleChangeResult}
            onFocus={() => setIsInputFocused(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setIsInputFocused(false);
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder={t('game.searchGame')}
            type="search"
          />
          {searchPhrase ? (
            <ButtonClose
              gray
              inCircle
              onClick={handleResetSearch}
              right={8}
              top={0}
            />
          ) : (
            <div className="search-icon">
              <IconComponent name="searchIcon" size="18px" />
            </div>
          )}
        </Form>
      </ScSearchForm>
      <ResultWithSimilar
        closeSearchModal={onClose}
        searchPhrase={searchTextDebounce}
      />
    </ScSearchModal>
  );
};

export const SearchModalStatic: FC<
  Required<Pick<ISearchModalProps, 'onClose'>>
> = ({ onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    sendSearchOverallAppearanceEvent();
  }, []);

  return (
    <StaticModal
      icon={<IconComponent name="magnifier" size="40px" />}
      isHideTitleOnMobileS
      isNoPaddingMob
      isPortal
      maxWidth={640}
      onClose={onClose}
      title={t('search')}
      verticalAlign={VerticalAlign.MIDDLE}
    >
      <SearchModal
        header={
          <>
            <IconComponent name="magnifier" size="40px" />
            {t('search')}
          </>
        }
        onClose={onClose}
      />
    </StaticModal>
  );
};

export const SearchModalTabBar: FC<
  Required<Omit<ISearchModalProps, 'header' | 'isNoPaddingMob'>>
> = ({ padding, onClose }) => {
  const { t } = useTranslation();
  const useCommonStore = useGetCommonStore();

  const tabBarHeight = useCommonStore.use.tabBarHeight();

  useEffect(() => {
    sendSearchOverallAppearanceEvent('tabbar');
  }, []);

  return (
    <ModalSelectContainer
      bottom={tabBarHeight - BOTTOM_GAP}
      gap={16}
      icon={<IconComponent name="magnifier" size="40px" />}
      onClose={onClose}
      padding={padding}
      title={t('search')}
    >
      <AnimatedWrapper>
        <AnimatedItem>
          <SearchModal isNoPaddingMob onClose={onClose} />
        </AnimatedItem>
      </AnimatedWrapper>
    </ModalSelectContainer>
  );
};
